// combatpro/runtime/EncounterOpsPanel.js
//
// Combat PRO - Encounter Ops panel (encounter-level DM Operations).
//
// Mounted on the Encounter page (the approved owner). Manages Combat Groups, shows
// the deterministic Live Threat readout, and generates the After-Action report from
// the actual live systems. Reads/writes encounter-level DM ops through
// CombatContext.setEncounterDmOps and reads live combat state; it owns no state and
// starts no parallel systems. No optional chaining, no nullish coalescing, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { familyArrayKey, activeFamilyOf } from '../encounter/encounterStoreEngine';
import AfterActionHandoffPanel from '../integration/AfterActionHandoffPanel';
import { newGroup, computeThreat, buildAfterAction, SIDES, SIDE_LABELS, OUTCOME_LABELS, OUT_OF_FIGHT,
  newTriggeredEffect, matchTriggers, TRIGGER_WHENS, TRIGGER_WHEN_LABELS, RESULT_KINDS, RESULT_KIND_LABELS,
  newQuickAction, QUICK_KINDS, QUICK_KIND_LABELS } from './dmOpsEngine';

function nz(v) { return (v === null || typeof v === 'undefined') ? '' : v; }

function formatReport(aa) {
  var lines = [];
  lines.push(aa.encounterName + ' - After-Action Report');
  var famLabel = aa.family ? (aa.family.charAt(0).toUpperCase() + aa.family.slice(1)) : 'Standard';
  if (aa.localSlot) { lines.push('Family: ' + famLabel + ' ' + aa.localSlot); }
  lines.push('Rounds elapsed: ' + aa.roundsElapsed);
  if (aa.waveCount) {
    lines.push('Waves: ' + aa.waveCount + (aa.waveStyle ? (' (' + aa.waveStyle + ')') : ''));
    (aa.waveSummary || []).forEach(function (w) { lines.push('  ' + w); });
  }
  if (aa.objective && aa.objective.trim()) { lines.push('Objective: ' + aa.objective + ' (' + aa.objectiveResolution + ')'); }
  lines.push('Party: ' + aa.partyCount + '   Enemies: ' + aa.enemyCount);
  OUT_OF_FIGHT.forEach(function (o) {
    if (aa.outcomes[o] && aa.outcomes[o].length) { lines.push(OUTCOME_LABELS[o] + ': ' + aa.outcomes[o].join(', ')); }
  });
  if (aa.resolvedEvents.length) { lines.push('Resolved events: ' + aa.resolvedEvents.join(', ')); }
  if (aa.hazards.length) { lines.push('Hazards: ' + aa.hazards.join(', ')); }
  lines.push('Combat log entries: ' + aa.logCount);
  return lines.join('\n');
}

function EncounterOpsPanel() {
  const ctx = useContext(CombatContext);
  const state = ctx.state;
  const [report, setReport] = useState('');
  const [afterAction, setAfterAction] = useState(null);

  const store = state.encounterStore;
  const ai = (store && typeof store.activeIndex === 'number') ? store.activeIndex : 0;
  const enc = (store && Array.isArray(store.encounters)) ? (store[familyArrayKey(activeFamilyOf(store))] || store.encounters)[ai] : null;
  const dmOps = (enc && enc.dmOps) ? enc.dmOps : { groups: [], triggeredEffects: [] };
  const groups = Array.isArray(dmOps.groups) ? dmOps.groups : [];
  const brief = (enc && enc.encounterBrief) ? enc.encounterBrief : null;
  const events = (brief && Array.isArray(brief.events)) ? brief.events : [];
  const pendingWaves = events.filter(function (e) { return e.status !== 'resolved' && (e.kind === 'wave' || e.kind === 'reinforcement'); }).length;

  const threat = computeThreat({ players: state.players, monsters: state.monsters, pendingWaves: pendingWaves });

  function addGroup() { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { groups: (o.groups || []).concat([newGroup('', 'enemy')]) }); }); }
  function updateGroup(id, patch) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { groups: (o.groups || []).map(function (g) { return g.id === id ? Object.assign({}, g, patch) : g; }) }); }); }
  function removeGroup(id) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { groups: (o.groups || []).filter(function (g) { return g.id !== id; }) }); }); }

  const triggers = Array.isArray(dmOps.triggeredEffects) ? dmOps.triggeredEffects : [];
  const dueTriggers = matchTriggers(triggers, { when: 'round-start', round: state.currentRound }).concat(matchTriggers(triggers, { when: 'round-end', round: state.currentRound }));
  function addTrigger() { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { triggeredEffects: (o.triggeredEffects || []).concat([newTriggeredEffect('manual')]) }); }); }
  function updateTrigger(id, patch) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { triggeredEffects: (o.triggeredEffects || []).map(function (x) { return x.id === id ? Object.assign({}, x, patch) : x; }) }); }); }
  function removeTrigger(id) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { triggeredEffects: (o.triggeredEffects || []).filter(function (x) { return x.id !== id; }) }); }); }

  const quickActions = Array.isArray(dmOps.quickActions) ? dmOps.quickActions : [];
  function addQuickAction(kind) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { quickActions: (o.quickActions || []).concat([newQuickAction(kind)]) }); }); }
  function updateQuickAction(id, patch) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { quickActions: (o.quickActions || []).map(function (q) { return q.id === id ? Object.assign({}, q, patch) : q; }) }); }); }
  function removeQuickAction(id) { ctx.setEncounterDmOps(function (o) { return Object.assign({}, o, { quickActions: (o.quickActions || []).filter(function (q) { return q.id !== id; }) }); }); }
  function runQuickAction(q) { if (ctx.addCombatLog) { ctx.addCombatLog('Quick action: ' + (q.label || QUICK_KIND_LABELS[q.kind]) + (q.detail ? (' - ' + q.detail) : ''), 'info'); } }

  function generateReport() {
    const aa = buildAfterAction({ encounter: enc || {}, family: activeFamilyOf(store), localSlot: ai + 1, players: state.players, monsters: state.monsters, log: state.combatLogEntries, round: state.currentRound });
    setAfterAction(aa);
    setReport(formatReport(aa));
  }
  function copyReport() {
    if (report && typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(report); }
  }

  return (
    <div>
      <div className="vi-xo-panel">
        <div className="vi-xo-title">Live Threat</div>
        <div className="vi-xo-threat">
          <span className={'vi-xo-band band-' + threat.band}>{threat.band}</span>
          <span className="vi-xo-stat">Party <strong>{threat.partySize}</strong></span>
          <span className="vi-xo-stat">Active enemies <strong>{threat.activeEnemies}</strong></span>
          <span className="vi-xo-stat">Defeated <strong>{threat.defeatedEnemies}</strong></span>
          <span className="vi-xo-stat">Enemy CR sum <strong>{threat.enemyCrSum}</strong></span>
          <span className="vi-xo-stat">Pending waves <strong>{threat.pendingWaves}</strong></span>
        </div>
      </div>

      <div className="vi-xo-panel">
        <div className="vi-xo-title">Combat Groups</div>
        {groups.length ? groups.map(function (g) {
          return (
            <div className="vi-xo-group" key={g.id}>
              <input className="vi-tr-input vi-eb-grow" placeholder="Group name (e.g. Entrance Guards, Ritualists, Boss, Wave 2, Captives)" value={nz(g.name)} onChange={function (e) { updateGroup(g.id, { name: e.target.value }); }} />
              <select className="vi-tr-input" value={g.side} onChange={function (e) { updateGroup(g.id, { side: e.target.value }); }}>
                {SIDES.map(function (s) { return <option key={s} value={s}>{SIDE_LABELS[s]}</option>; })}
              </select>
              <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove group" onClick={function () { removeGroup(g.id); }}>{'✖'}</button>
            </div>
          );
        }) : <div className="vi-tr-empty">No combat groups. Add encounter groups such as Entrance Guards, Ritualists, Boss or Wave 2, then assign monsters to them from Selection.</div>}
        <div className="vi-eb-chiprow"><button type="button" className="vi-tr-btn" onClick={addGroup}>+ Add group</button></div>
      </div>

      <div className="vi-xo-panel">
        <div className="vi-xo-title">Quick Actions</div>
        <p className="vi-eb-help">Structured, one-tap references to existing systems (dice, weapon, ability, spell, damage, condition, hazard, note). Running one logs it to the Combat Log as a reminder. No scripting.</p>
        {quickActions.length ? (
          <div className="vi-eb-chiprow">
            {quickActions.map(function (q) { return <button key={'run' + q.id} type="button" className="vi-tr-chip is-on" onClick={function () { runQuickAction(q); }}>{q.label || QUICK_KIND_LABELS[q.kind]}</button>; })}
          </div>
        ) : null}
        {quickActions.map(function (q) {
          return (
            <div className="vi-xo-group" key={q.id}>
              <input className="vi-tr-input vi-eb-grow" placeholder="Quick action label (e.g. Recharge breath, Fire spreads)" value={(q.label === null || typeof q.label === 'undefined') ? '' : q.label} onChange={function (e) { updateQuickAction(q.id, { label: e.target.value }); }} />
              <select className="vi-tr-input" value={q.kind} onChange={function (e) { updateQuickAction(q.id, { kind: e.target.value }); }}>
                {QUICK_KINDS.map(function (k) { return <option key={k} value={k}>{QUICK_KIND_LABELS[k]}</option>; })}
              </select>
              <input className="vi-tr-input vi-eb-grow" placeholder="Detail (optional)" value={(q.detail === null || typeof q.detail === 'undefined') ? '' : q.detail} onChange={function (e) { updateQuickAction(q.id, { detail: e.target.value }); }} />
              <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove" onClick={function () { removeQuickAction(q.id); }}>{'\u2716'}</button>
            </div>
          );
        })}
        <div className="vi-eb-chiprow">
          <span className="vi-eb-mini-label">Add</span>
          {QUICK_KINDS.map(function (k) { return <button key={k} type="button" className="vi-tr-chip" onClick={function () { addQuickAction(k); }}>{'+ ' + QUICK_KIND_LABELS[k]}</button>; })}
        </div>
      </div>

      <div className="vi-xo-panel">
        <div className="vi-xo-title">Triggered Effects</div>
        <p className="vi-eb-help">Structured, bounded triggers surfaced as DM reminders when their condition is reached. They never auto-execute: you confirm and apply the result. No scripting.</p>
        {dueTriggers.length ? (
          <div className="vi-eb-runstrip">
            {dueTriggers.map(function (x) { return <div className="vi-eb-run-item" key={'due' + x.id}><span className="vi-eb-run-label">Reached</span><span className="vi-eb-run-val">{x.name || TRIGGER_WHEN_LABELS[x.when]} <span className="vi-eb-due">now</span></span></div>; })}
          </div>
        ) : null}
        {triggers.length ? triggers.map(function (x) {
          return (
            <div className="vi-eb-item" key={x.id}>
              <div className="vi-eb-item-top">
                <input className="vi-tr-input vi-eb-grow" placeholder="Triggered effect name" value={(x.name === null || typeof x.name === 'undefined') ? '' : x.name} onChange={function (e) { updateTrigger(x.id, { name: e.target.value }); }} />
                <button type="button" className={'vi-tr-chip' + (x.enabled !== false ? ' is-on' : '')} onClick={function () { updateTrigger(x.id, { enabled: !(x.enabled !== false) }); }}>Enabled</button>
                <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Remove" onClick={function () { removeTrigger(x.id); }}>{'\u2716'}</button>
              </div>
              <div className="vi-eb-item-row">
                <span className="vi-eb-mini-label">When</span>
                <select className="vi-tr-input" value={x.when} onChange={function (e) { updateTrigger(x.id, { when: e.target.value }); }}>
                  {TRIGGER_WHENS.map(function (w) { return <option key={w} value={w}>{TRIGGER_WHEN_LABELS[w]}</option>; })}
                </select>
                {(x.when === 'round-start' || x.when === 'round-end') ? (
                  <label className="vi-eb-inline">Round <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" value={(x.round === null || typeof x.round === 'undefined') ? '' : x.round} onChange={function (e) { updateTrigger(x.id, { round: e.target.value === '' ? null : parseInt(e.target.value, 10) }); }} /></label>
                ) : null}
                {x.when === 'hp-threshold' ? (
                  <label className="vi-eb-inline">At <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" max="100" value={(x.hpPct === null || typeof x.hpPct === 'undefined') ? '' : x.hpPct} onChange={function (e) { updateTrigger(x.id, { hpPct: e.target.value === '' ? null : parseInt(e.target.value, 10) }); }} /> % HP</label>
                ) : null}
                <span className="vi-eb-mini-label">Result</span>
                <select className="vi-tr-input" value={x.resultKind} onChange={function (e) { updateTrigger(x.id, { resultKind: e.target.value }); }}>
                  {RESULT_KINDS.map(function (rk) { return <option key={rk} value={rk}>{RESULT_KIND_LABELS[rk]}</option>; })}
                </select>
              </div>
              <div className="vi-eb-item-row">
                <input className="vi-tr-input vi-eb-grow" placeholder="Detail (what the DM applies)" value={(x.detail === null || typeof x.detail === 'undefined') ? '' : x.detail} onChange={function (e) { updateTrigger(x.id, { detail: e.target.value }); }} />
              </div>
            </div>
          );
        }) : <div className="vi-tr-empty">No triggered effects. Add start/end of turn or round, on-hit, save, HP-threshold or manual reminders.</div>}
        <div className="vi-eb-chiprow"><button type="button" className="vi-tr-btn" onClick={addTrigger}>+ Add triggered effect</button></div>
      </div>

      <div className="vi-xo-panel">
        <div className="vi-xo-title">After-Action Report</div>
        <div className="vi-eb-chiprow">
          <button type="button" className="vi-tr-primary" onClick={generateReport}>Generate report</button>
          {report ? <button type="button" className="vi-tr-btn" onClick={copyReport}>Copy</button> : null}
        </div>
        {report ? <div className="vi-xo-report">{report}</div> : null}
        {afterAction ? <AfterActionHandoffPanel afterAction={afterAction} /> : null}
      </div>
    </div>
  );
}

export default EncounterOpsPanel;
